import React, { useCallback, useContext } from 'react'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { nonNullable } from 'lib/array/arrayUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOffer } from 'tripPlanner/hooks/api'
import {
  CruiseItem,
  SavedCruiseData,
  TourItem,
} from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  formatSingleDateAndTimeRange,
  getOfferImage,
  isCustomItem,
  isLeBookmark,
} from 'tripPlanner/utils'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import EditTripItem from '../TripModal/TripItems/Edit'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useNavigateTo from 'hooks/useNavigateTo'
import { isTourV1Offer, isTourV2Offer } from 'lib/offer/offerTypes'
import TourUrgencyLabels from './TourUrgencyLabels'
import TourV2PriceAndCTAs from './TourV2PriceAndCTAs'
import { NarrowCardOnlyBookedLabel } from './common/BookedLabel'
import TourV1PriceAndCTAs from './TourV1PriceAndCTAs'
import ItemPriceAndCTAs from './common/ItemPriceAndCTAs'
import BookedOfferDetailsCTA from './CTAContent/BookedOfferDetailsCTA'
import CruisePriceAndCTAs from './CruisePriceAndCTAs'
import { ItemMetadata } from 'tripPlanner/models'

function isV2CruiseItem(item: CruiseItem | TourItem): item is CruiseItem & { savedItemData: Extract<SavedCruiseData, { offerType: 'cruise' }> } {
  return item.type === 'CRUISE' && item.savedItemData?.offerType === 'cruise'
}

function locationText(item: TourItem | CruiseItem) {
  if (item.endPlace && item.endPlace.name !== item.startPlace.name) {
    return `Starts in ${item.startPlace.name}, Ends in ${item.endPlace.name}`
  } else {
    return item.startPlace.name
  }
}

interface Props {
  item: TourItem | CruiseItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailTourCruiseCard({ item, variant, tripNameLabel }: Props) {
  const offerId = 'code' in item ? item.code : undefined
  // The query type includes many offer types, but this card is only used for tour offers
  const { data: _offer } = useOffer(offerId)
  const offer = _offer && (_offer.parentType === 'tour' || _offer.parentType === 'tour_v2' || _offer.parentType === 'cruise') ? _offer : undefined

  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = !!tripContext?.viewAsTemplate
  const timeFormat = useRegionTimeFormat()
  const offerLink = isLeBookmark(item) ? buildProductLink(item, !viewAsTemplate, { offer }) : undefined

  const { id: imgId, url: imgSrc } = getOfferImage(offer)

  const timeDescription =
    item.startDate && item.endDate && !item.endDate.isSame(item.startDate) ?
      formatShortDateRange({
        startDate: item.startDate,
        endDate: item.endDate,
      }) :
      formatSingleDateAndTimeRange(item, timeFormat)
  const timeAndOccupancyText = nonNullable([
    timeDescription,
    // occupanciesText,
  ])
    .filter((text) => text.length > 0)
    .join(', ')

  const startPlaceId = 'id' in item.startPlace ? item.startPlace.id : undefined

  const openModal = useOpenModal()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const navigateTo = useNavigateTo()

  const onClickAddTripItemDates = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: itemSectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const dateWarningIcon =
  variant === 'tripSummary' ?
    <OutsideTripDatesIcon
      item={item}
      changeItemDates={onClickAddTripItemDates}
    /> :
    undefined

  let bottomContent: JSX.Element | null = null

  if (variant === 'itinerary') {
    // No bottom content for itinerary
  } else if (item.isBooked && item.confirmationCode) {
    bottomContent = <ItemPriceAndCTAs>
      <div className="cta-area"><BookedOfferDetailsCTA item={item} size="medium" /></div>
    </ItemPriceAndCTAs>
  } else if (isLeBookmark(item) && isTourV1Offer(offer)) {
    bottomContent = (
      <TourV1PriceAndCTAs
        item={item}
        offer={offer}
        viewAsTemplate={viewAsTemplate}
        variant={variant}
      />
    )
  } else if (isLeBookmark(item) && item.type === 'TOUR' && isTourV2Offer(offer)) {
    bottomContent = (
      <TourV2PriceAndCTAs
        item={item}
        offer={offer}
        viewAsTemplate={viewAsTemplate}
        variant={variant}
      />
    )
  } else if (isLeBookmark(item) && isV2CruiseItem(item)) {
    bottomContent = (
      <CruisePriceAndCTAs
        item={item}
        viewAsTemplate={viewAsTemplate}
        variant={variant}
      />
    )
  }

  return (
    <DetailCardLayout data-testid="detail-card-tour">
      <DetailCardLayout.Image>
        <ItemCardImage
          item={item}
          name={item.startPlace.name}
          imgId={imgId ?? item.savedItemData?.imageIds?.[0]}
          imgSrc={imgSrc}
          placeId={startPlaceId}
        />
      </DetailCardLayout.Image>
      <DetailCardLayout.Content>
        {canEdit && <CornerControls item={item} variant={variant} />}
        {item.isBooked && <NarrowCardOnlyBookedLabel className="bottom-margin" />}
        <VerticalSpacer gap={8}>
          <VerticalSpacer gap={4}>
            <BodyText variant="small" colour="neutral-two">
              {locationText(item)}
            </BodyText>
            <ItemTitle icon={ItemMetadata[item.type].icon}>{item.name}</ItemTitle>
          </VerticalSpacer>
          {isTourV2Offer(offer) && <TourUrgencyLabels offer={offer} />}
          {timeAndOccupancyText && (
            <BodyTextBlock variant="medium" startIcon={<LineNotesIcon />} endIcon={dateWarningIcon}>
              {timeAndOccupancyText}
            </BodyTextBlock>
          )}
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
          <PrintOnlyAddressAndNotes item={item} />
          {bottomContent}
        </VerticalSpacer>
      </DetailCardLayout.Content>
    </DetailCardLayout>
  )
}

export default DetailTourCruiseCard
